import type { PipingSystem } from '../types';

function addElementId(target: Set<string>, value: unknown) {
  const normalized = String(value ?? '').trim();
  if (!/^-?\d+$/.test(normalized)) {
    return;
  }

  try {
    if (BigInt(normalized) > 0n) {
      target.add(normalized);
    }
  } catch {
    // Ignore values that cannot be represented as Revit element IDs.
  }
}

function parseElementIdBucket(value: unknown): string[] {
  const ids = new Set<string>();

  if (Array.isArray(value)) {
    value.forEach((item) => addElementId(ids, item));
    return Array.from(ids);
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    addElementId(ids, value);
    return Array.from(ids);
  }

  if (typeof value !== 'string') {
    return [];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      parsed.forEach((item) => addElementId(ids, item));
      return Array.from(ids);
    }
  } catch {
    // Fall back to delimiter parsing.
  }

  trimmed.split(/[,\n;|]+/).forEach((item) => addElementId(ids, item));
  return Array.from(ids);
}

function getPipingSystemModelElementIds(system: PipingSystem): string[] {
  const ids = new Set<string>();
  const systemRecord = system as Record<string, unknown>;
  const props = systemRecord.__props as Record<string, unknown> | undefined;

  [
    system.elementIds,
    systemRecord.ElementIds,
    systemRecord.elementIds,
    props?.ElementIds,
    props?.elementIds,
    props?.element_ids,
  ].forEach((bucket) => {
    parseElementIdBucket(bucket).forEach((id) => ids.add(id));
  });

  return Array.from(ids);
}

export function collectPipingSystemModelElementIds(systems: PipingSystem[]): string[] {
  const ids = new Set<string>();
  systems.forEach((system) => {
    getPipingSystemModelElementIds(system).forEach((id) => ids.add(id));
  });
  return Array.from(ids);
}

export function getPipingSystemModelElementCount(system: PipingSystem): number {
  return getPipingSystemModelElementIds(system).length;
}
